'use client'
import { type FC, useMemo } from 'react'
import { Box, HStack, SimpleGrid, type StackProps, VStack, Wrap, WrapItem } from '@chakra-ui/react'
import { ContentContainer, GrandNextLink, GrandText } from 'ui/common'
import CategoryWithSubCategories from '../CategoryWithSubCategories'
import Thumbnails from './Thumbnails'
import {
  type SlideOutCategoriesDesktopContextState,
  SlideOutCategoriesDesktopProvider
} from './SlideOutCategoriesDesktopContext'

export interface ISlideOutCategoriesProps
  extends SlideOutCategoriesDesktopContextState,
    StackProps {
  columnWrapApproach?: 'grid' | 'wrap'
}

export const MenuStandardSlideOutCategoriesDesktop: FC<ISlideOutCategoriesProps> = ({
  currentTopCategory,
  paths,
  menuCategoriesThumbnails = true,
  topNavSlideOutFeaturedCategories = 3,
  columnWrapApproach = 'grid',
  ...props
}) => {
  const menuCategories = useMemo(
    () => currentTopCategory?.subCategories ?? [],
    [currentTopCategory]
  )

  const renderCategories = () => {
    if (columnWrapApproach === 'grid') {
      return (
        <SimpleGrid columns={{ base: 1, md: 5 }} gap={4} w="full">
          {menuCategories.map?.((category, index) => (
            // eslint-disable-next-line react/no-array-index-key -- id could be null or undefined
            <Box key={`${index}-navCategoryGroup1-${category?.id}`}>
              <CategoryWithSubCategories {...{ category, paths }} />
            </Box>
          ))}
        </SimpleGrid>
      )
    }

    return (
      <Wrap spacing={4} w="full">
        {menuCategories.map?.((category, index) => (
          // eslint-disable-next-line react/no-array-index-key -- id could be null or undefined
          <WrapItem key={`${index}-navCategoryGroup1-${category?.id}`} className="blaitem">
            <CategoryWithSubCategories {...{ category, paths }} />
          </WrapItem>
        ))}
      </Wrap>
    )
  }

  return (
    <SlideOutCategoriesDesktopProvider
      value={{
        currentTopCategory,
        paths,
        menuCategoriesThumbnails,
        topNavSlideOutFeaturedCategories
      }}
    >
      <ContentContainer
        borderColor="primary"
        borderTopWidth="1px"
        minH="20vh"
        py={0}
        w="100%"
        {...props}
      >
        <HStack align="flex-start" py={6} spacing={2} w="100%">
          <VStack flex={1} spacing={0}>
            <Box w="full">
              <GrandNextLink
                aria-label={`category ${currentTopCategory?.name?.toUpperCase?.()}`}
                fontSize="sm"
                href={`${paths.categories}/${currentTopCategory?.slug}`}
              >
                <GrandText pb={4}>{currentTopCategory?.name?.toUpperCase?.()}</GrandText>
              </GrandNextLink>
            </Box>
            <Box w="full" maxH="60vh" overflowY="auto">
              {renderCategories()}
            </Box>
          </VStack>

          {menuCategoriesThumbnails && <Thumbnails flex={0} {...{ menuCategories }} />}
        </HStack>
      </ContentContainer>
    </SlideOutCategoriesDesktopProvider>
  )
}
